import React from 'react'
import { applicationSchema, collectErrors, normaliseLinkedIn, normaliseUrl } from '../../lib/careersSchema'
import type { FieldErrors, ApplicationInput } from '../../lib/careersSchema'
import { uploadApplicationFile, submitApplication } from '../../lib/careersApi'
import type { UploadField } from '../../lib/careersApi'
import { track } from '../../lib/analytics'
import { getPosition } from '../../data/careers'
import type { CoverLetterMode } from './CoverLetterToggle'

export interface ApplicationValues {
  firstName: string
  lastName: string
  email: string
  phone: string
  country: string
  city: string
  linkedinUrl: string
  portfolioUrl: string
  positionSlug: string
  preferredStart: string
  availability: string
  referralSource: string
  referralSourceOther: string
  coverLetterMode: CoverLetterMode | ''
  coverLetterText: string
  additionalInfo: string
  consentGiven: boolean
}

type Status = 'idle' | 'uploading' | 'submitting' | 'done'

/** Validation keys mapped to the element that should receive focus, in form order. */
export const FIELD_FOCUS_ORDER: [string, string][] = [
  ['firstName', 'app-first'],
  ['lastName', 'app-last'],
  ['email', 'app-email'],
  ['phone', 'app-phone'],
  ['country', 'app-country'],
  ['city', 'app-city'],
  ['linkedinUrl', 'app-linkedin'],
  ['portfolioUrl', 'app-portfolio'],
  ['positionSlug', 'app-position'],
  ['preferredStart', 'app-start'],
  ['availability', 'app-availability'],
  ['referralSource', 'app-referral'],
  ['referralSourceOther', 'app-referral-other'],
  ['cvS3Key', 'app-cv'],
  ['cvFilename', 'app-cv'],
  ['coverLetterMode', 'cover-mode'],
  ['coverLetterFile', 'cover-file'],
  ['coverLetterText', 'cover-text'],
  ['additionalInfo', 'app-additional'],
  ['consentGiven', 'app-consent'],
]

function focusElement(id: string) {
  const target =
    id === 'cover-mode'
      ? document.querySelector<HTMLInputElement>('input[name="cover-letter-mode"]')
      : document.getElementById(id)
  if (!target) return
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' })
  target.focus({ preventScroll: true })
}

export function useApplicationForm(initialPosition: string) {
  const [values, setValues] = React.useState<ApplicationValues>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    linkedinUrl: '',
    portfolioUrl: '',
    positionSlug: getPosition(initialPosition)?.isOpen ? initialPosition : '',
    preferredStart: '',
    availability: '',
    referralSource: '',
    referralSourceOther: '',
    coverLetterMode: '',
    coverLetterText: '',
    additionalInfo: '',
    consentGiven: false,
  })
  const [cvFile, setCvFile] = React.useState<File | null>(null)
  const [coverFile, setCoverFile] = React.useState<File | null>(null)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [status, setStatus] = React.useState<Status>('idle')
  const [progress, setProgress] = React.useState<{ cv: number | null; cover: number | null }>({
    cv: null,
    cover: null,
  })
  const [generalError, setGeneralError] = React.useState<string | null>(null)
  const [emailSent, setEmailSent] = React.useState(false)
  const generalErrorRef = React.useRef<HTMLDivElement>(null)

  // A file that already reached storage is not uploaded again on a retry.
  const uploaded = React.useRef(new Map<File, { key: string; filename: string }>())

  const set = <K extends keyof ApplicationValues>(key: K, value: ApplicationValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }))
    setErrors((current) => (current[key] ? { ...current, [key]: undefined } : current))
  }

  const setMode = (mode: CoverLetterMode) => {
    // Only one mode is live at a time, so switching clears the other input.
    setValues((current) => ({ ...current, coverLetterMode: mode, coverLetterText: mode === 'upload' ? '' : current.coverLetterText }))
    if (mode === 'write') setCoverFile(null)
    setProgress((current) => ({ ...current, cover: null }))
    setErrors((current) => ({ ...current, coverLetterMode: undefined, coverLetterFile: undefined, coverLetterText: undefined }))
  }

  const setCv = (file: File | null) => {
    setCvFile(file)
    setProgress((current) => ({ ...current, cv: null }))
    setErrors((current) => ({ ...current, cvS3Key: undefined, cvFilename: undefined }))
  }

  const setCover = (file: File | null) => {
    setCoverFile(file)
    setProgress((current) => ({ ...current, cover: null }))
    setErrors((current) => ({ ...current, coverLetterFile: undefined }))
  }

  const buildPayload = (
    cv: { key: string; filename: string } | null,
    cover: { key: string; filename: string } | null,
  ) => ({
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone,
    country: values.country,
    city: values.city,
    linkedinUrl: values.linkedinUrl ? normaliseLinkedIn(values.linkedinUrl) : '',
    portfolioUrl: values.portfolioUrl ? normaliseUrl(values.portfolioUrl) : '',
    positionSlug: values.positionSlug,
    preferredStart: values.preferredStart,
    availability: values.availability,
    referralSource: values.referralSource,
    referralSourceOther: values.referralSource === 'Other' ? values.referralSourceOther : '',
    cvS3Key: cv?.key ?? '',
    cvFilename: cv?.filename ?? '',
    coverLetterMode: values.coverLetterMode,
    coverLetterS3Key: values.coverLetterMode === 'upload' ? cover?.key : undefined,
    coverLetterFilename: values.coverLetterMode === 'upload' ? cover?.filename : undefined,
    coverLetterText: values.coverLetterMode === 'write' ? values.coverLetterText.trim() : undefined,
    additionalInfo: values.additionalInfo,
    consentGiven: values.consentGiven,
  })

  const upload = async (file: File, field: UploadField, progressKey: 'cv' | 'cover') => {
    const cached = uploaded.current.get(file)
    if (cached) {
      setProgress((current) => ({ ...current, [progressKey]: 100 }))
      return cached
    }
    const result = await uploadApplicationFile(file, field, (pct) =>
      setProgress((current) => ({ ...current, [progressKey]: pct })),
    )
    uploaded.current.set(file, result)
    return result
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setGeneralError(null)

    // Validate everything before any byte leaves the browser, using placeholder
    // keys for files that are attached but not yet uploaded.
    const pending = (file: File | null) => (file ? { key: 'pending', filename: file.name } : null)
    const check = applicationSchema.safeParse(buildPayload(pending(cvFile), pending(coverFile)))
    if (!check.success) {
      const found = collectErrors(check.error)
      setErrors(found)
      const first = FIELD_FOCUS_ORDER.find(([key]) => found[key])
      if (first) window.setTimeout(() => focusElement(first[1]), 0)
      return
    }
    setErrors({})

    try {
      setStatus('uploading')
      const cv = await upload(cvFile as File, 'cv', 'cv')
      const cover =
        values.coverLetterMode === 'upload' && coverFile ? await upload(coverFile, 'cover_letter', 'cover') : null

      setStatus('submitting')
      const payload = applicationSchema.parse(buildPayload(cv, cover)) as ApplicationInput
      const result = await submitApplication(payload)

      if (!result.ok) throw new Error(result.error)

      setEmailSent(result.emailSent)
      setStatus('done')
      track('application_submitted', { source: 'careers', role: values.positionSlug })
      window.scrollTo({ top: 0 })
    } catch (error) {
      setStatus('idle')
      setGeneralError(
        error instanceof Error && error.message
          ? error.message
          : 'We could not submit your application. Your answers are still here, so please try again.',
      )
      window.setTimeout(() => {
        generalErrorRef.current?.scrollIntoView({ block: 'center' })
        generalErrorRef.current?.focus()
      }, 0)
    }
  }

  return {
    values,
    set,
    setMode,
    cvFile,
    setCv,
    coverFile,
    setCover,
    errors,
    status,
    busy: status === 'uploading' || status === 'submitting',
    progress,
    generalError,
    generalErrorRef,
    emailSent,
    onSubmit,
  }
}
